import React from "react";
import { DD_MM_YYYY } from "src/helpers";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { CheckInsGroupsOptions, WarehouseCheckInColumnHeader, WarehouseCheckInFileType } from "src/enums";
import { TableColumn, UpdateWarehouseCheckInOpsNoteParams, WarehouseCheckIn } from "src/types";
import { EditOpsNote } from "src/components";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import { useAuthorisation } from "src/context";
import { Info, FileText } from "lucide-react";
import { Button } from "src/shadcn/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import { Link } from "react-router-dom";

interface WarehouseCheckInsDashTableProps {
  data: WarehouseCheckIn[];
  columnsToHide: WarehouseCheckInColumnHeader[];
  onUpdateOpsNote: (params: UpdateWarehouseCheckInOpsNoteParams) => void;
  downloadFile: (params: {
    warehouseCheckInId: string;
    timestamp: string;
    type: WarehouseCheckInFileType.CHECK_IN_IMAGES;
  }) => void;
  isFileDownloading?: boolean;
  group?: CheckInsGroupsOptions;
}

const WarehouseCheckInsDashTable: React.FC<WarehouseCheckInsDashTableProps> = ({
  data,
  columnsToHide,
  onUpdateOpsNote,
  downloadFile,
  isFileDownloading,
  group
}) => {
  const { userRole } = useAuthorisation();

  const warehouseCheckInsDashTableColumns: TableColumn<WarehouseCheckInColumnHeader, WarehouseCheckIn>[] = [
    {
      header: WarehouseCheckInColumnHeader.CHECK_IN_ID,
      column: (row: WarehouseCheckIn) => row.id,
      styles: { whiteSpace: "nowrap" }
    },
    {
      header: WarehouseCheckInColumnHeader.TIMESTAMP,
      column: (row: WarehouseCheckIn) => (row.timestamp ? DD_MM_YYYY.formatDate(new Date(row.timestamp)) : undefined),
      styles: { whiteSpace: "nowrap" }
    },
    {
      header: WarehouseCheckInColumnHeader.STATUS,
      column: (row: WarehouseCheckIn) => (
        <span style={{ display: "flex" }}>
          <StatusPill
            status={row.status}
            linkPath={
              row.flaggedIssues?.length && userRole !== "WAREHOUSE"
                ? row.type === "PURCHASE_ORDER"
                  ? `${ROUTES_CONFIG.receivingDiscrepancies.path}?id=${row.id}`
                  : `${ROUTES_CONFIG.unknownArrivals.path}?id=${row.id}&timestamp=${row.timestamp}`
                : undefined
            }
          />
        </span>
      )
    },
    {
      header: WarehouseCheckInColumnHeader.ASN,
      column: (row: WarehouseCheckIn) => row.asn,
      styles: { whiteSpace: "nowrap" }
    },
    {
      header: WarehouseCheckInColumnHeader.VENDOR,
      column: (row: WarehouseCheckIn) => row.vendorName
    },
    {
      header: WarehouseCheckInColumnHeader.ORIGIN,
      column: (row: WarehouseCheckIn) => row.origin
    },
    {
      header: WarehouseCheckInColumnHeader.ORDER_SENDER_EMAIL,
      column: (row: WarehouseCheckIn) => row.orderSenderEmail
    },
    {
      header: WarehouseCheckInColumnHeader.CHECK_IN_EMAIL,
      column: (row: WarehouseCheckIn) => row.checkInUserEmail
    },
    // ...(group === CheckInsGroupsOptions.CHECK_IN_ID
    //   ? []
    //   : [
    //       {
    //         header: WarehouseCheckInColumnHeader.MORE_INFO,
    //         column: (row: WarehouseCheckIn) => (
    //           <PageLinkButton
    //             path={`${ROUTES_CONFIG.warehouseCheckIns.path}?id=${row.id}&timestamp=${row.timestamp}`}
    //           />
    //         )
    //       }
    //     ]),
    // {
    //   header: WarehouseCheckInColumnHeader.IMAGE,
    //   column: (row: WarehouseCheckIn) =>
    //     row.hasImages ? (
    //       <FileDownloadButton
    //         handleDownload={() =>
    //           downloadFile({
    //             warehouseCheckInId: row.id,
    //             timestamp: row.timestamp,
    //             type: WarehouseCheckInFileType.CHECK_IN_IMAGES
    //           })
    //         }
    //         isDisabled={isFileDownloading}
    //       />
    //     ) : undefined,
    //   styles: { textAlign: "center" }
    // },
    {
      header: WarehouseCheckInColumnHeader.UPDATED_AT,
      column: (row: WarehouseCheckIn) => (row.updatedAt ? DD_MM_YYYY.formatDate(new Date(row.updatedAt)) : undefined),
      styles: { whiteSpace: "nowrap" }
    },
    {
      header: WarehouseCheckInColumnHeader.OPS_NOTE,
      column: (row: WarehouseCheckIn) => (
        <EditOpsNote<WarehouseCheckIn>
          id={row.id}
          timestamp={row.timestamp ?? ""}
          opsNote={row.additionalInfo?.operationsNote}
          onUpdateOpsNote={onUpdateOpsNote}
        />
      ),
      styles: { textAlign: "center" }
    },
    {
      header: WarehouseCheckInColumnHeader.ACTIONS,
      column: (row: WarehouseCheckIn) => (
        <TooltipProvider>
          <div className="flex items-center justify-center gap-1">
            {/* More Info */}
            {group !== CheckInsGroupsOptions.CHECK_IN_ID && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`${ROUTES_CONFIG.warehouseCheckIns.path}?id=${row.id}&timestamp=${row.timestamp}`}
                    className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                  >
                    <Info className="h-4 w-4 text-muted-600" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>More Info</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Image Download */}
            {row.hasImages && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-secondary-100"
                    onClick={() =>
                      downloadFile({
                        warehouseCheckInId: row.id,
                        timestamp: row.timestamp,
                        type: WarehouseCheckInFileType.CHECK_IN_IMAGES
                      })
                    }
                    disabled={isFileDownloading}
                  >
                    <FileText className="h-4 w-4 text-muted-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download Check-in Images</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      ),
      styles: { textAlign: "center" }
    }
  ];

  return (
    <Table<WarehouseCheckInColumnHeader, WarehouseCheckIn>
      data={data}
      columns={warehouseCheckInsDashTableColumns}
      columnsToHide={columnsToHide}
    />
  );
};

export { WarehouseCheckInsDashTable };
